/**
 * ChildrenPanel - create and list child profiles.
 *
 * Deliberately minimal: name and date of birth, nothing else. The child record
 * is what every piece of engine progress hangs off (speech_attempt.child_id,
 * box_score.child_id), so until one exists nothing can be recorded at all.
 *
 * Date of birth rather than birth year because the difference between 24 and
 * 35 months spans three phases of the framework - a birth year is too coarse
 * to place a two-year-old.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { childrenApi } from '../utils/api';

export default function ChildrenPanel() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const data = await childrenApi.list();
      setChildren(data.children || []);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setSaving(true);
    setFormError(null);

    try {
      const { child } = await childrenApi.create({
        name: name.trim(),
        birthDate: birthDate || null
      });
      setChildren((prev) => [...prev, child]);
      setName('');
      setBirthDate('');
      setShowForm(false);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold text-gray-900">Children</h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => { setShowForm(true); setFormError(null); }}
            className="btn-primary text-sm px-4 py-2"
          >
            + Add a child
          </button>
        )}
      </div>

      {loading && <p className="text-gray-500">Loading…</p>}

      {loadError && (
        <div className="card border-2 border-red-200 bg-red-50 text-red-700 mb-4">
          {loadError}
        </div>
      )}

      {!loading && !loadError && children.length === 0 && !showForm && (
        <div className="card text-center">
          <div className="text-4xl mb-3">👶</div>
          <p className="text-gray-600 mb-4">
            No children yet. Add one to start tracking progress across games.
          </p>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
            Add your first child
          </button>
        </div>
      )}

      {children.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
          {children.map((child) => (
            <div key={child.id} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-gray-900">{child.name}</p>
                  <p className="text-sm text-gray-500">{describeAge(child.birthDate)}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">🧒</span>
                </div>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                Phase {child.currentPhase} · Level {child.currentLevel}
              </p>
              {/* The chart is per child and lives off the child record, so this
                  is the only place it can honestly be reached from. */}
              <Link
                to={`/progress/${child.id}`}
                className="mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-semibold hover:bg-[#22507c] transition-colors"
              >
                See progress
              </Link>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="card">
          <h3 className="font-display text-lg font-bold text-gray-900 mb-4">Add a child</h3>

          {formError && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {formError}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label" htmlFor="child-name">Name</label>
              <input
                id="child-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                required
                autoFocus
              />
            </div>

            <div>
              <label className="label" htmlFor="child-dob">Date of birth</label>
              <input
                id="child-dob"
                type="date"
                className="input"
                value={birthDate}
                max={today}
                onChange={(e) => setBirthDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button type="submit" className="btn-primary" disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : 'Add child'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setShowForm(false); setFormError(null); }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** "3 years old" / "18 months old", or a plain note when no date was given. */
function describeAge(birthDate) {
  if (!birthDate) return 'Date of birth not set';

  const dob = new Date(birthDate);
  if (Number.isNaN(dob.getTime())) return 'Date of birth not set';

  const now = new Date();
  let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
  if (now.getDate() < dob.getDate()) months -= 1;
  if (months < 0) return 'Date of birth not set';

  if (months < 24) return `${months} month${months === 1 ? '' : 's'} old`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} old`;
}
